import React, { useEffect } from 'react';
import { useReactiveVar } from '@apollo/client';
import PropTypes from 'prop-types';

import { Layout, Tabs, Col, Button } from 'antd';
import { LayoutMenu, ElementsMenu, FontsMenu, LibraryMenu } from './components';
import DoubleArrowIcon from 'components/Icons/DoubleArrowIcon';
import CloseIcon from 'components/Icons/CloseIcon';
import { fontListEnglish, fontListHebrew } from 'pages/Proposal/constants/constants';
import { fontLoadedVar } from 'graphql/cache';
import { loadProposalFont } from 'helpers/proposal';
import { useProposalContext } from 'context/proposal';

import './ToolBarSideMenu.scss';

const { TabPane } = Tabs;

const ToolBarSideMenu = ({
  collapse,
  setCollapse,
  libraryType,
  dragStart,
  activeSideMenuKey,
  changeToolBarSideMenu,
  handleToolbarElements,
  dragEnd,
  showAttachmentModal,
  setDroppable,
  isSection,
  addNewSectionFromLibrary,
  sectionRef,
  openVariablesList,
  setOpenVariablesList,
  setDisablePreview,
  user,
}) => {
  const { proposal } = useProposalContext();
  const language = proposal?.language?.toLowerCase() || 'english';

  const fontLoaded = useReactiveVar(fontLoadedVar);
  const handlePrelistLoad = (langChange) => {
    let prelist = [];

    if (language === 'hebrew' || langChange === 'hebrew') {
      prelist = fontListHebrew;
    } else {
      prelist = fontListEnglish;
    }

    if (!fontLoaded) {
      fontLoadedVar(true);
      loadProposalFont(prelist, langChange || language, user);
    }
  };

  useEffect(() => {
    handlePrelistLoad(language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sectionName = isSection ? proposal?.draft?.sectionorder[0] || 'header' : 'header';

  return (
    <Layout.Sider
      className={`toolbar-side-menu main-menu ${collapse && 'ant-layout-sider-is-collapsed'}`}>
      {!collapse && (
        <div className="toolbar-side-menu-closing-mask" onClick={() => setCollapse(true)} />
      )}
      <Button className="white-btn close-btn medium-btn below-sm" onClick={() => setCollapse(true)}>
        <CloseIcon />
      </Button>
      <Tabs
        className="tabs toolbar-side-menu-tab"
        hideAdd
        activeKey={activeSideMenuKey}
        onTabClick={(key) => changeToolBarSideMenu({ key })}>
        <TabPane tab="ELEMENTS" key="elements">
          <ElementsMenu
            handleToolbarElements={handleToolbarElements}
            dragStart={dragStart}
            dragEnd={dragEnd}
            showAttachmentModal={showAttachmentModal}
            setDroppable={setDroppable}
            openVariablesList={openVariablesList}
            setOpenVariablesList={setOpenVariablesList}
            isSection={isSection}
          />
        </TabPane>
        <TabPane tab="LIBRARY" key="library">
          <LibraryMenu
            sectionRef={sectionRef}
            libraryType={libraryType}
            dragStart={dragStart}
            dragEnd={dragEnd}
            setDroppable={setDroppable}
            isSection={isSection}
            sectionName={sectionName}
            addNewSectionFromLibrary={addNewSectionFromLibrary}
            handleToolbarElements={handleToolbarElements}
          />
        </TabPane>
        <TabPane tab="FONT" key="font">
          <FontsMenu
            draft={proposal?.draft || proposal}
            user={user}
            sectionName={sectionName}
            isSection={isSection}
          />
        </TabPane>
        {!isSection && (
          <TabPane tab="LAYOUT" key="layout">
            <LayoutMenu setDisablePreview={setDisablePreview} />
          </TabPane>
        )}
      </Tabs>
      <Col className={`ant-layout-sider-collapse ${collapse && `ant-layout-sider-collapse-open`}`}>
        <DoubleArrowIcon
          className={collapse ? 'ant-layout-sider-collapse-expand' : ''}
          onClick={() => setCollapse(!collapse)}
        />
      </Col>
    </Layout.Sider>
  );
};

ToolBarSideMenu.defaultProps = {
  collapse: false,
  setCollapse: () => {},
  activeSideMenuKey: 'elements',
  isSection: false,
  disablePreview: false,
  setDisablePreview: () => {},
  user: '',
};

ToolBarSideMenu.propTypes = {
  handleToolbarElements: PropTypes.func.isRequired,
  activeSideMenuKey: PropTypes.string,
  changeToolBarSideMenu: PropTypes.func.isRequired,
  libraryType: PropTypes.string.isRequired,
  dragStart: PropTypes.func.isRequired,
  collapse: PropTypes.bool,
  isSection: PropTypes.bool,
  disablePreview: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  setDisablePreview: PropTypes.func,
  setCollapse: PropTypes.func,
  showAttachmentModal: PropTypes.func.isRequired,
  dragEnd: PropTypes.func.isRequired,
  addNewSectionFromLibrary: PropTypes.func.isRequired,
  sectionRef: PropTypes.instanceOf(Object).isRequired,
  user: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
};

export default ToolBarSideMenu;
